// Las imágenes de un mensaje dentro de la hoja de vista previa (04-11-2026): la que se pulsó, ajustada a la hoja; si el
// mensaje trae varias, su carril y ←/→ entre ellas. El tamaño: + / − / 0, Ctrl+rueda y pellizco.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Pildora, useDespertar, useGestosZoom, type ControlCuerpo, type PropsCuerpo } from './piezas';
import { useVistaPrevia } from './vistaPrevia';
import { efectivo, multiplicar } from './zoom';

export const VisorImagenes = forwardRef<ControlCuerpo, PropsCuerpo>(function VisorImagenes({ apertura, elemento, zoom, alZoom, carril }, ref) {
  const ir = useVistaPrevia((s) => s.ir);
  const [zona, setZona] = useState<HTMLDivElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const total = apertura.elementos.length;
  const i = apertura.indice;
  useEffect(() => {
    setNatural(null);
  }, [elemento.clave]);
  const ajuste = useCallback(() => {
    if (!zona || !natural) return 100;
    return Math.min(1, Math.min((zona.clientWidth - 48) / natural.w, (zona.clientHeight - 48) / natural.h)) * 100;
  }, [zona, natural]);
  useImperativeHandle(
    ref,
    () => ({
      anterior: () => ir(i - 1),
      siguiente: () => ir(i + 1),
      inicio: () => ir(0),
      fin: () => ir(total - 1),
      ajuste,
      desplazable: () => zona,
    }),
    [ir, i, total, ajuste, zona],
  );
  useGestosZoom(zona, (f) => alZoom(multiplicar(zoom, ajuste(), f)));
  const despierta = useDespertar(zona);
  const ancho = zoom === 'ajustar' || !natural ? undefined : (natural.w * efectivo(zoom, 100)) / 100;
  return (
    <div className="flex min-h-0 flex-1">
      {carril ? (
        <nav aria-label={t('oficinaV2.vistaPrevia.imagenes')} className="w-44 shrink-0 space-y-3 overflow-y-auto bg-oficina-fondo px-6 py-4">
          {apertura.elementos.map((el, n) => (
            <button
              key={el.clave}
              type="button"
              onClick={() => ir(n)}
              aria-current={n === i}
              aria-label={el.titulo}
              className={cn('block w-32 overflow-hidden rounded-o-s bg-oficina-elevada outline-none focus-visible:ring-2 focus-visible:ring-oficina-texto', n === i ? 'ring-2 ring-oficina-texto' : 'opacity-80 hover:opacity-100')}
            >
              <img src={el.url} alt="" className="aspect-square w-full object-cover" loading="lazy" />
            </button>
          ))}
        </nav>
      ) : null}
      <div className="relative min-w-0 flex-1 bg-oficina-fondo">
        <div ref={setZona} data-autofoco tabIndex={-1} className="flex h-full w-full touch-pan-x touch-pan-y overflow-auto outline-none">
          <img
            key={elemento.clave}
            src={elemento.url}
            alt={elemento.titulo}
            onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth || 1, h: e.currentTarget.naturalHeight || 1 })}
            style={ancho ? { width: ancho, maxWidth: 'none', maxHeight: 'none' } : undefined}
            className={cn('m-auto shrink-0 rounded-o-s shadow-o-flota', !ancho && 'max-h-full max-w-full object-contain p-6')}
          />
        </div>
        {total > 1 ? <Pildora actual={i + 1} total={total} alAnterior={() => ir(i - 1)} alSiguiente={() => ir(i + 1)} despierta={despierta} /> : null}
      </div>
    </div>
  );
});
